import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, TrendingUp, Loader2, Award, Target } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import StreakCard from '../components/StreakCard';

const moduleNames = {
  grammar_cloze: 'Grammar Cloze',
  vocabulary_cloze: 'Vocabulary Cloze',
  grammar_mcq: 'Grammar MCQ',
  vocabulary_mcq: 'Vocabulary MCQ',
  synthesis: 'Synthesis',
  editing: 'Editing',
  comprehension_cloze: 'Comprehension',
  antonyms_synonyms: 'Antonyms & Synonyms',
  'antonyms_synonyms:synonyms': 'Antonyms & Synonyms — Synonyms',
  'antonyms_synonyms:antonyms': 'Antonyms & Synonyms — Antonyms',
  regular_irregular_verbs: 'Regular & Irregular Verbs',
  'regular_irregular_verbs:regular': 'Regular & Irregular Verbs — Regular',
  'regular_irregular_verbs:irregular': 'Regular & Irregular Verbs — Irregular'
};

const ProgressPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const data = await api.getProgress();
      setProgress(data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const calculateAccuracy = (correct, attempted) => {
    if (attempted === 0) return 0;
    return Math.round((correct / attempted) * 100);
  };

  const overallAccuracy = calculateAccuracy(progress?.total_correct || 0, progress?.total_attempted || 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="progress-back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold text-emerald-600" data-testid="progress-title">
                  My Progress
                </h1>
                <p className="text-sm text-slate-500">{user?.name} - {user?.level}</p>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="text-center py-16" data-testid="progress-loading">
            <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-lg text-slate-600">Loading your progress...</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Streak Card */}
            <div className="mb-8">
              <StreakCard />
            </div>

            {/* Overall Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6 rounded-3xl border-2 border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-emerald-500 p-3 rounded-2xl">
                    <Target className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-slate-800">Questions Attempted</h3>
                </div>
                <p className="text-4xl font-heading font-bold text-emerald-600" data-testid="total-attempted">
                  {progress?.total_attempted || 0}
                </p>
              </Card>

              <Card className="p-6 rounded-3xl border-2 border-slate-200 bg-gradient-to-br from-green-50 to-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-green-500 p-3 rounded-2xl">
                    <Award className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-slate-800">Correct Answers</h3>
                </div>
                <p className="text-4xl font-heading font-bold text-green-600" data-testid="total-correct">
                  {progress?.total_correct || 0}
                </p>
              </Card>

              <Card className="p-6 rounded-3xl border-2 border-slate-200 bg-gradient-to-br from-amber-50 to-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-amber-500 p-3 rounded-2xl">
                    <TrendingUp className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-slate-800">Accuracy</h3>
                </div>
                <p className="text-4xl font-heading font-bold text-amber-600" data-testid="overall-accuracy">
                  {overallAccuracy}%
                </p>
              </Card>
            </div>

            {/* By Question Type */}
            {progress?.by_type && Object.keys(progress.by_type).length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-heading font-bold text-slate-800 mb-4">Progress by Question Type</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(progress.by_type).map(([type, stats]) => {
                    const accuracy = calculateAccuracy(stats.correct, stats.attempted);
                    const safeKey = type.replace(/:/g, '-');
                    return (
                      <Card key={type} className="p-6 rounded-3xl border-2 border-slate-200" data-testid={`type-card-${safeKey}`}>
                        <h3 className="text-lg font-heading font-bold text-slate-800 mb-3">{moduleNames[type] || type}</h3>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Attempted:</span>
                          <span className="font-bold text-slate-800" data-testid={`type-attempted-${safeKey}`}>{stats.attempted}</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Correct:</span>
                          <span className="font-bold text-green-600" data-testid={`type-correct-${safeKey}`}>{stats.correct}</span>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-bold text-slate-700">Accuracy</span>
                            <span className="text-sm font-bold text-emerald-600" data-testid={`type-accuracy-${safeKey}`}>{accuracy}%</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-3">
                            <div
                              className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${accuracy}%` }}
                            />
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* By Level */}
            {progress?.by_level && Object.keys(progress.by_level).length > 0 && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-800 mb-4">Progress by Level</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(progress.by_level).map(([level, stats]) => {
                    const accuracy = calculateAccuracy(stats.correct, stats.attempted);
                    return (
                      <Card key={level} className="p-6 rounded-3xl border-2 border-slate-200" data-testid={`level-card-${level}`}>
                        <h3 className="text-lg font-heading font-bold text-slate-800 mb-3">Primary {level.replace('P', '')}</h3>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Attempted:</span>
                          <span className="font-bold text-slate-800">{stats.attempted}</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Correct:</span>
                          <span className="font-bold text-green-600">{stats.correct}</span>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-bold text-slate-700">Accuracy</span>
                            <span className="text-sm font-bold text-emerald-600">{accuracy}%</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-3">
                            <div
                              className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${accuracy}%` }}
                            />
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {progress?.total_attempted === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
                data-testid="progress-empty"
              >
                <TrendingUp className="w-24 h-24 text-slate-300 mx-auto mb-4" />
                <h2 className="text-2xl font-heading font-bold text-slate-800 mb-2">No progress yet</h2>
                <p className="text-slate-600 mb-6">Start practicing to track your improvement!</p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg font-bold tactile-button border-emerald-700"
                  data-testid="start-practicing-button"
                >
                  Start Practicing
                </Button>
              </motion.div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ProgressPage;